import { useCallback, useState } from 'react'
import { Box, Text, Progress } from '@chakra-ui/react'
import { useDocuments } from '../hooks/useDocuments'

export default function FileUpload() {
  const { uploadDocument, isUploading, uploadProgress } = useDocuments()
  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const files = Array.from(e.dataTransfer.files)
      const pdfFile = files.find((f) => f.type === 'application/pdf')

      if (pdfFile) {
        uploadDocument(pdfFile)
      }
    },
    [uploadDocument]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && file.type === 'application/pdf') {
        uploadDocument(file)
      }
      e.target.value = ''
    },
    [uploadDocument]
  )

  return (
    <Box position="relative">
      <label htmlFor="file-upload" style={{ display: 'block' }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          p={4}
          border="2px dashed"
          borderColor={isDragging ? 'blue.400' : 'gray.300'}
          borderRadius="md"
          bg={isDragging ? 'blue.50' : 'white'}
          cursor={isUploading ? 'not-allowed' : 'pointer'}
          transition="all 0.2s"
          _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <UploadIcon />
          <Text fontSize="sm" color="gray.600" textAlign="center">
            {isUploading ? 'Uploading...' : 'Drop PDF here or click to upload'}
          </Text>
          {isUploading && uploadProgress > 0 && (
            <Progress.Root value={uploadProgress} w="full" size="sm">
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          )}
        </Box>
      </label>
      <input
        id="file-upload"
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileSelect}
        disabled={isUploading}
        style={{ display: 'none' }}
      />
    </Box>
  )
}

function UploadIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
    </svg>
  )
}
